<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import MetricHelp from '../../components/MetricHelp.vue';

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  formatCurrency: {
    type: Function,
    required: true,
  },
});

const { t } = useI18n();

const cards = computed(() => [
  {
    key: 'total',
    metricKey: 'LEADS',
    label: t('CRM_METRICS.LEADS.NAME'),
    value: props.data.total_deals || 0,
    icon: 'i-lucide-layout-grid',
    color: 'blue',
  },
  {
    key: 'won',
    metricKey: 'SALES',
    label: t('CRM_METRICS.SALES.NAME'),
    value: props.data.won?.count || 0,
    subValue: props.formatCurrency(props.data.won?.value || 0),
    icon: 'i-lucide-trophy',
    color: 'green',
  },
  {
    key: 'lost',
    metricKey: 'LOSSES',
    label: t('CRM_METRICS.LOSSES.NAME'),
    value: props.data.lost?.count || 0,
    subValue: props.formatCurrency(props.data.lost?.value || 0),
    icon: 'i-lucide-x-circle',
    color: 'red',
  },
  {
    key: 'open',
    metricKey: 'OPEN_PIPELINE',
    label: t('CRM_METRICS.OPEN_PIPELINE.NAME'),
    value: props.data.open?.count || 0,
    subValue: props.formatCurrency(props.data.open?.value || 0),
    icon: 'i-lucide-clock',
    color: 'amber',
  },
  {
    key: 'conversion',
    metricKey: 'WIN_RATE',
    label: t('CRM_METRICS.WIN_RATE.NAME'),
    value: `${props.data.conversion_rate || 0}%`,
    icon: 'i-lucide-trending-up',
    color: 'purple',
  },
  {
    key: 'avgValue',
    metricKey: 'AVERAGE_TICKET',
    label: t('CRM_METRICS.AVERAGE_TICKET.NAME'),
    value: props.formatCurrency(props.data.average_deal_value || 0),
    icon: 'i-lucide-banknote',
    color: 'teal',
  },
  {
    key: 'avgTime',
    label: t('DEAL_REPORTS.SUMMARY.AVG_CLOSE_TIME'),
    value: `${props.data.average_close_time || 0} ${t('DEAL_REPORTS.SUMMARY.DAYS')}`,
    icon: 'i-lucide-calendar',
    color: 'indigo',
  },
]);

const colorClasses = {
  blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
  green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
  red: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
  amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
  purple:
    'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
  teal: 'bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400',
  indigo:
    'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400',
};
</script>

<template>
  <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
    <div
      v-for="card in cards"
      :key="card.key"
      class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-4"
    >
      <div class="flex items-center gap-3 mb-3">
        <div
          class="w-10 h-10 rounded-lg flex items-center justify-center"
          :class="colorClasses[card.color]"
        >
          <span :class="card.icon" class="text-xl" />
        </div>
      </div>
      <p
        class="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 mb-1"
      >
        {{ card.label }}
        <MetricHelp v-if="card.metricKey" :metric-key="card.metricKey" />
      </p>
      <p class="text-xl font-bold text-slate-900 dark:text-white">
        {{ card.value }}
      </p>
      <p
        v-if="card.subValue"
        class="text-sm text-slate-600 dark:text-slate-300 mt-1"
      >
        {{ card.subValue }}
      </p>
    </div>
  </div>
</template>
